import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { lastValueFrom } from 'rxjs';
import { apiConfiguration } from '../../../configurations/api-configuration';
import { HttpResponse } from '../types/HttpResponse';
import { AccessService } from './AccessService';

export type DashboardNotificationKind =
  | 'calendar'
  | 'client'
  | 'directory'
  | 'event'
  | 'finance'
  | 'kanban'
  | 'mailing'
  | 'order'
  | 'system'
  | 'telegram'
  | 'user';

export interface DashboardNotification {
  id: string;
  title: string;
  description: string;
  actor: string;
  kind: DashboardNotificationKind;
  createdAt: string;
  read: boolean;
}

interface TokenPayload { username?: string; }

interface PanelLog {
  id: string;
  userId: string | null;
  username: string;
  event: string;
  message: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

@Injectable()
export class NotificationService {
  private readonly storageKey = 'dashboardNotifications';
  private readonly maxNotifications = 200;
  private readonly panelLogsUrl = `${apiConfiguration.baseApiUrl}/users/logs`;
  private readonly localNotificationsSubject = new BehaviorSubject<DashboardNotification[]>(this.restore());
  private readonly notificationsSubject = new BehaviorSubject<DashboardNotification[]>(this.localNotificationsSubject.value);
  private panelLogNotifications: DashboardNotification[] = [];

  public readonly notifications$ = this.notificationsSubject.asObservable();

  public constructor(
    private http: HttpClient,
    private accessService: AccessService,
  ) { }

  public getNotifications(): DashboardNotification[] {
    return this.notificationsSubject.value;
  }

  public async refresh(): Promise<void> {
    const token = this.accessService.getToken();
    if (!token) {
      this.panelLogNotifications = [];
      this.publish();
      return;
    }

    try {
      const response = await lastValueFrom(this.http.get<HttpResponse<PanelLog[]>>(
        `${this.panelLogsUrl}?limit=${this.maxNotifications.toString()}`,
        { headers: this.getHeaders() },
      ));

      this.panelLogNotifications = response.status === 'successful'
        ? response.response.map((log) => this.mapPanelLog(log))
        : [];
    } catch {
      this.panelLogNotifications = [];
    }

    this.publish();
  }

  public notify(
    kind: DashboardNotificationKind,
    title: string,
    description: string,
  ): void {
    const notification: DashboardNotification = {
      id: `${Date.now().toString()}-${Math.random().toString(16).slice(2)}`,
      title,
      description,
      actor: this.getCurrentUsername(),
      kind,
      createdAt: new Date().toISOString(),
      read: false,
    };

    this.storeLocal([
      notification,
      ...this.localNotificationsSubject.value,
    ].slice(0, this.maxNotifications));
  }

  public markAsRead(notificationId: string): void {
    this.storeLocal(this.localNotificationsSubject.value.map((notification) => (
      notification.id === notificationId
        ? { ...notification, read: true }
        : notification
    )));
  }

  public markAllAsRead(): void {
    this.storeLocal(this.localNotificationsSubject.value.map((notification) => ({
      ...notification,
      read: true,
    })));
  }

  public clear(): void {
    this.storeLocal([]);
  }

  private storeLocal(notifications: DashboardNotification[]): void {
    this.localNotificationsSubject.next(notifications);
    localStorage.setItem(this.storageKey, JSON.stringify(notifications));
    this.publish();
  }

  private publish(): void {
    const localIds = new Set(this.localNotificationsSubject.value.map((notification) => notification.id));
    const notifications = [
      ...this.localNotificationsSubject.value,
      ...this.panelLogNotifications.filter((notification) => !localIds.has(notification.id)),
    ]
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .slice(0, this.maxNotifications);

    this.notificationsSubject.next(notifications);
  }

  private restore(): DashboardNotification[] {
    const notifications = localStorage.getItem(this.storageKey);
    if (!notifications) {
      return [];
    }

    try {
      return (JSON.parse(notifications) as DashboardNotification[])
        .map((notification) => ({
          ...notification,
          kind: notification.kind ?? 'system',
          read: Boolean(notification.read),
        }))
        .slice(0, this.maxNotifications);
    } catch {
      localStorage.removeItem(this.storageKey);
      return [];
    }
  }

  private getCurrentUsername(): string {
    const token = this.accessService.getToken();
    if (!token) {
      return 'Пользователь';
    }

    const payload = this.decodeTokenPayload(token);
    return payload?.username ?? 'Пользователь';
  }

  private getHeaders(): HttpHeaders {
    const token = this.accessService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }

  private mapPanelLog(log: PanelLog): DashboardNotification {
    return {
      id: `panel-log-${log.id}`,
      title: this.getPanelLogTitle(log),
      description: log.message || 'Действие в панели',
      actor: log.username || 'Система',
      kind: this.getPanelLogKind(log),
      createdAt: log.createdAt,
      read: true,
    };
  }

  private getPanelLogTitle(log: PanelLog): string {
    const titles: Record<string, string> = {
      api_token_refreshed: 'API token обновлен',
      dashboard_action: 'Действие в дашборде',
      login_blocked: 'Вход заблокирован',
      login_failed: 'Ошибка входа',
      login_success: 'Вход в систему',
      password_changed: 'Пароль изменен',
      user_blocked: 'Пользователь заблокирован',
      user_created: 'Пользователь добавлен',
      user_updated: 'Пользователь обновлен',
    };

    return titles[log.event] ?? 'Системное событие';
  }

  private getPanelLogKind(log: PanelLog): DashboardNotificationKind {
    if (log.event.startsWith('user_') || log.event === 'password_changed' || log.event === 'api_token_refreshed') {
      return 'user';
    }

    if (log.event === 'dashboard_action') {
      return this.inferKindFromMessage(log.message);
    }

    return 'system';
  }

  private inferKindFromMessage(message: string | null): DashboardNotificationKind {
    const value = message?.toLowerCase() ?? '';
    if (value.includes('клиент') || value.includes('заметка клиента')) {
      return 'client';
    }
    if (value.includes('заказ')) {
      return 'order';
    }
    if (value.includes('рассыл')) {
      return 'mailing';
    }
    if (value.includes('telegram')) {
      return 'telegram';
    }
    if (value.includes('событ') || value.includes('билет')) {
      return 'event';
    }
    if (value.includes('расход') || value.includes('взаиморасчет')) {
      return 'finance';
    }
    if (value.includes('календар')) {
      return 'calendar';
    }
    if (value.includes('задач')) {
      return 'kanban';
    }
    if (value.includes('команд') || value.includes('сезон') || value.includes('чемпионат')) {
      return 'directory';
    }

    return 'system';
  }

  private decodeTokenPayload(token: string): TokenPayload | null {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    try {
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
      return JSON.parse(atob(paddedPayload)) as TokenPayload;
    } catch {
      return null;
    }
  }
}
